import React, { useEffect, useRef } from 'react';
import { Paper, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Chart, registerables } from 'chart.js';

// Register Chart.js components
Chart.register(...registerables);

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    height: '100%'
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  chartContainer: {
    position: 'relative',
    height: 300
  }
}));

const MetricsChart = ({ 
  title, 
  data = [], 
  label = 'Value',
  color = '#1976d2',
  yAxisLabel = '%'
}) => {
  const classes = useStyles();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || data.length === 0) return;
    
    const ctx = canvasRef.current.getContext('2d');
    
    // Destroy existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
    }
    
    // Create new chart
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => new Date(d.timestamp).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })),
        datasets: [{
          label,
          data: data.map(d => d.value),
          borderColor: color,
          backgroundColor: color + '20',
          tension: 0.4,
          fill: true,
          pointRadius: 2,
          pointHoverRadius: 5
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => `${context.parsed.y.toFixed(1)}${yAxisLabel}`
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: (value) => value + yAxisLabel
            }
          },
          x: {
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 10
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
    });
    
    // Cleanup
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [data, label, color, yAxisLabel]);
  
  if (data.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={300}
        >
          <Typography color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Paper>
    );
  }
  
  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.title}>
        {title}
      </Typography>
      <Box className={classes.chartContainer}>
        <canvas ref={canvasRef} />
      </Box>
    </Paper>
  );
};

export default MetricsChart;

